import { Button, Tag, useAlert } from '@ads/components-react';
import { BanknotesIcon, ChevronRightIcon, CreditCardIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

import type {
  EnrollmentFinancialDiscount,
  EnrollmentFinancialSummary,
} from '../../../types/enrollment';

export interface EnrollmentFinancialTabProps {
  readonly financialSummary?: EnrollmentFinancialSummary | null;
  readonly onOpenPayments: () => void;
}

function SectionCard({
  children,
  header,
}: {
  children: React.ReactNode;
  header?: React.ReactNode;
}) {
  return (
    <div className="rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface overflow-hidden">
      {header && (
        <div className="border-b-hairline border-ctx-layout-border px-xxs py-quark">{header}</div>
      )}
      <div className="px-xxs py-xxxs">{children}</div>
    </div>
  );
}

function SectionTitle({
  icon: Icon,
  title,
  description,
}: {
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  title: string;
  description?: string;
}) {
  return (
    <div className="flex items-center gap-mini py-nano">
      {Icon && <Icon className="h-xxxs w-xxxs text-ctx-content-placeholder" />}
      <div className="flex flex-col">
        <span className="ts-paragraph-xs !font-medium text-ctx-content-title">{title}</span>
        {description && (
          <span className="ts-caption-xxs text-ctx-content-placeholder">{description}</span>
        )}
      </div>
    </div>
  );
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
}

function getDaysFromToday(dateStr: string): number {
  const date = new Date(dateStr);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);
  return Math.round((date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
}

function StatCard({
  label,
  value,
  description,
}: {
  label: string;
  value: string;
  description: string;
}) {
  return (
    <div className="flex flex-col gap-quark rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xxs">
      <span className="ts-caption-xxs tracking-wider text-ctx-content-placeholder uppercase">
        {label}
      </span>
      <span className="ts-heading-sm text-ctx-content-title">{value}</span>
      <span className="ts-caption-xxs text-ctx-content-placeholder">{description}</span>
    </div>
  );
}

function InfoRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-quark">
      <span className="ts-caption-xxs tracking-wider text-ctx-content-placeholder uppercase">
        {label}
      </span>
      <div className="ts-paragraph-xs font-medium text-ctx-content-title">{children}</div>
    </div>
  );
}

function DiscountRow({ discount }: { discount: EnrollmentFinancialDiscount }) {
  const { t } = useTranslation('personManagement');
  return (
    <div className="grid grid-cols-2 gap-xxs rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-body p-xxs md:grid-cols-4">
      <InfoRow label={t('enrollmentFinancial.discountType')}>
        {discount.discountTypeText ?? '—'}
      </InfoRow>
      <InfoRow label={t('enrollmentFinancial.discountPercentage')}>
        {discount.percentage != null ? `${discount.percentage}%` : '—'}
      </InfoRow>
      <InfoRow label={t('enrollmentFinancial.discountDescription')}>
        {discount.description ?? '—'}
      </InfoRow>
      <InfoRow label={t('enrollmentFinancial.discountCompany')}>
        {discount.companyName ?? '—'}
      </InfoRow>
    </div>
  );
}

/**
 * Tab financeira da gestão de matrícula.
 * Redireciona ao módulo Pagamento Individual e exibe resumo financeiro.
 *
 * @param financialSummary - Dados financeiros resumidos da matrícula
 * @param onOpenPayments - Callback para abrir o módulo de Pagamento Individual
 */
export function EnrollmentFinancialTab({ financialSummary }: EnrollmentFinancialTabProps) {
  const { t } = useTranslation('personManagement');
  const { alert } = useAlert();

  const buildNextDueDescription = (): string => {
    if (!financialSummary?.nextDueDate || !financialSummary.nextDueValue) return '—';
    const value = formatCurrency(financialSummary.nextDueValue);
    const days = getDaysFromToday(financialSummary.nextDueDate);
    if (days === 0) return t('enrollmentFinancial.statNextDueToday', { value });
    if (days < 0)
      return t('enrollmentFinancial.statNextDueOverdue', {
        value,
        days: Math.abs(days),
      });
    return t('enrollmentFinancial.statNextDueDaysDesc', { value, days });
  };

  return (
    <div className="flex flex-col gap-xxs bg-ctx-layout-body py-xs">
      <SectionCard
        header={
          <SectionTitle
            icon={BanknotesIcon}
            title={t('enrollmentFinancial.sectionTitle')}
            description={t('enrollmentFinancial.sectionDescription')}
          />
        }
      >
        <div className="flex flex-col gap-xxs">
          {/* CTA para Pagamento Individual */}
          <div className="flex items-center justify-between gap-xs rounded-sm bg-ctx-layout-surface p-xxs">
            <div className="flex flex-col gap-xxxs">
              <span className="ts-caption-xxs tracking-wider text-ctx-content-placeholder uppercase">
                {t('enrollmentFinancial.ctaBadge')}
              </span>
              <h3 className="ts-heading-sm text-ctx-content-title">
                {t('enrollmentFinancial.ctaHeading')}
              </h3>
              <p className="ts-paragraph-xs text-ctx-content-base">
                {t('enrollmentFinancial.ctaDescriptionPre')}{' '}
                <strong className="!font-semibold">
                  {t('enrollmentFinancial.ctaDescriptionBold')}
                </strong>
                {t('enrollmentFinancial.ctaDescriptionPost')}
              </p>
            </div>
            <div className="shrink-0">
              <Button
                hierarchy="primary"
                size="md"
                trailingIcon={ChevronRightIcon}
                onClick={() =>
                  alert({
                    alertType: 'warning',
                    title: t('enrollmentFinancial.ctaNotIntegrated'),
                  })
                }
              >
                {t('enrollmentFinancial.ctaButton')}
              </Button>
            </div>
          </div>

          {/* Resumo financeiro */}
          {financialSummary ? (
            <div className="grid grid-cols-2 gap-xxxs md:grid-cols-4">
              <StatCard
                label={t('enrollmentFinancial.statTotalLabel')}
                value={formatCurrency(financialSummary.totalValue)}
                description={
                  financialSummary.hasEnrollmentFee
                    ? t('enrollmentFinancial.statTotalDescWithFee', {
                        count: financialSummary.totalInstallments,
                      })
                    : t('enrollmentFinancial.statTotalDescNoFee', {
                        count: financialSummary.totalInstallments,
                      })
                }
              />
              <StatCard
                label={t('enrollmentFinancial.statPaidLabel')}
                value={formatCurrency(financialSummary.paidValue)}
                description={
                  financialSummary.hasEnrollmentFee
                    ? t('enrollmentFinancial.statPaidDescWithFee', {
                        count: financialSummary.paidInstallments,
                      })
                    : t('enrollmentFinancial.statPaidDescNoFee', {
                        count: financialSummary.paidInstallments,
                      })
                }
              />
              <StatCard
                label={t('enrollmentFinancial.statRemainingLabel')}
                value={formatCurrency(financialSummary.remainingValue)}
                description={t('enrollmentFinancial.statRemainingDesc', {
                  count: financialSummary.remainingInstallments,
                })}
              />
              {financialSummary.nextDueDate && (
                <StatCard
                  label={t('enrollmentFinancial.statNextDueLabel')}
                  value={new Date(financialSummary.nextDueDate).toLocaleDateString('pt-BR')}
                  description={buildNextDueDescription()}
                />
              )}
            </div>
          ) : (
            <p className="ts-paragraph-xs text-ctx-content-placeholder">
              {t('enrollmentFinancial.noFinancialData')}
            </p>
          )}
        </div>
      </SectionCard>

      {/* ─ Detalhes do plano ─ */}
      {financialSummary && (
        <SectionCard
          header={
            <SectionTitle
              icon={CreditCardIcon}
              title={t('enrollmentFinancial.planSectionTitle')}
              description={t('enrollmentFinancial.planSectionDescription')}
            />
          }
        >
          <div className="grid grid-cols-2 gap-xxs md:grid-cols-3">
            {financialSummary.descriptionPaymentMethod && (
              <InfoRow label={t('enrollmentFinancial.fieldPaymentMethod')}>
                {financialSummary.descriptionPaymentMethod}
              </InfoRow>
            )}
            {financialSummary.paymentInCashOrInstallments && (
              <InfoRow label={t('enrollmentFinancial.fieldPaymentModality')}>
                {financialSummary.paymentInCashOrInstallments}
              </InfoRow>
            )}
            {financialSummary.planSummary && (
              <InfoRow label={t('enrollmentFinancial.fieldPlanSummary')}>
                {financialSummary.planSummary}
              </InfoRow>
            )}
            <InfoRow label={t('enrollmentFinancial.fieldIsScholarshipHolder')}>
              <Tag
                label={financialSummary.isScholarshipHolder ? 'Sim' : 'Não'}
                size="sm"
                status={financialSummary.isScholarshipHolder ? 'positive' : 'default'}
                custom={{ text: 'text-nowrap' }}
              />
            </InfoRow>
            <InfoRow label={t('enrollmentFinancial.fieldPoloAllowedFinancial')}>
              <Tag
                label={
                  financialSummary.isPoloAllowedToChangeFinancial
                    ? t('enrollmentFinancial.fieldPoloAllowedYes')
                    : t('enrollmentFinancial.fieldPoloAllowedNo')
                }
                size="sm"
                status={financialSummary.isPoloAllowedToChangeFinancial ? 'positive' : 'default'}
                custom={{ text: 'text-nowrap' }}
              />
            </InfoRow>
            {financialSummary.registrationTaxExpirationDate && (
              <InfoRow label={t('enrollmentFinancial.fieldRegistrationTax')}>
                <div className="flex flex-col gap-quark">
                  <span>
                    {new Date(financialSummary.registrationTaxExpirationDate).toLocaleDateString(
                      'pt-BR',
                    )}
                  </span>
                  <span className="ts-caption-xxs text-ctx-content-placeholder">
                    {financialSummary.poloCanChangeRegistrationTax
                      ? t('enrollmentFinancial.fieldPoloCanChange')
                      : t('enrollmentFinancial.fieldPoloCannotChange')}
                  </span>
                </div>
              </InfoRow>
            )}
            {financialSummary.firstTuitionExpirationDate && (
              <InfoRow label={t('enrollmentFinancial.fieldFirstTuition')}>
                <div className="flex flex-col gap-quark">
                  <span>
                    {new Date(financialSummary.firstTuitionExpirationDate).toLocaleDateString(
                      'pt-BR',
                    )}
                  </span>
                  <span className="ts-caption-xxs text-ctx-content-placeholder">
                    {financialSummary.poloCanChangeFirstTuition
                      ? t('enrollmentFinancial.fieldPoloCanChange')
                      : t('enrollmentFinancial.fieldPoloCannotChange')}
                  </span>
                </div>
              </InfoRow>
            )}
          </div>
        </SectionCard>
      )}

      {/* ─ Descontos ─ */}
      {financialSummary && (
        <SectionCard
          header={
            <SectionTitle
              title={t('enrollmentFinancial.discountsSectionTitle')}
              description={t('enrollmentFinancial.discountsSectionDescription')}
            />
          }
        >
          {financialSummary.discounts.length > 0 ? (
            <div className="flex flex-col gap-xxxs">
              {financialSummary.discounts.map((d, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: desconto sem id próprio
                <DiscountRow key={i} discount={d} />
              ))}
            </div>
          ) : (
            <p className="ts-paragraph-xs text-ctx-content-placeholder">
              {t('enrollmentFinancial.noDiscounts')}
            </p>
          )}
        </SectionCard>
      )}
    </div>
  );
}
